import React from "react";
import { hasVisibleHtml } from "helpers/contentful/contentfulFieldToHtml";
import { buildNativeMapsDirectionsUrl } from "helpers/geocode/meetPlaceDistance";

const MeetVisitPlaceCard = ({ place, category, distancesLoading, venueQuery, showDistance = false }) => {
  const destination = place.address?.includes("KY")
    ? place.address
    : place.address
      ? `${place.address}, KY`
      : "";
  const directionsUrl = destination
    ? buildNativeMapsDirectionsUrl(venueQuery, destination)
    : "";

  const className = `meet-x__visit-place-card${
    place.url ? " meet-x__visit-place-card--linked" : ""
  }${place.isPartner ? " meet-x__visit-place-card--partner" : ""}`;

  const cardDetails = (
    <>
      <div className="meet-x__visit-place-head">
        <h4 className="meet-x__visit-place-name">{place.name}</h4>
        {place.isPartner && (
          <span className="meet-x__visit-partner-badge">Meet partner</span>
        )}
      </div>
      {place.cuisine && <p className="meet-x__visit-place-cuisine">{place.cuisine}</p>}
      {place.address && (
        <p className="meet-x__visit-place-address">{place.address}</p>
      )}
      {showDistance &&
        (place.distance ? (
          <p className="meet-x__visit-place-distance">{place.distance}</p>
        ) : (
          place.address &&
          distancesLoading && (
            <p className="meet-x__visit-place-distance meet-x__visit-place-distance--pending">
              Calculating drive…
            </p>
          )
        ))}
      {place.note && <p className="meet-x__visit-place-note">{place.note}</p>}
    </>
  );

  return (
    <article className={className}>
      {place.url ? (
        <a
          href={place.url}
          target="_blank"
          rel="noopener noreferrer"
          className="meet-x__visit-place-main"
          data-track="meet_tourism_click"
          data-track-location="visit_bardstown"
          data-track-label={category}
          data-track-item={place.name}
        >
          {cardDetails}
        </a>
      ) : (
        <div className="meet-x__visit-place-main">{cardDetails}</div>
      )}
      {directionsUrl && (
        <a
          href={directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="meet-x__visit-place-directions"
          data-track="meet_tourism_click"
          data-track-location="visit_bardstown"
          data-track-label={`${category}_directions`}
          data-track-item={place.name}
        >
          Get driving directions →
        </a>
      )}
    </article>
  );
};

const MeetVisitPlacesBlock = ({
  heading,
  intro,
  places,
  category,
  distancesLoading,
  venueQuery,
  showDistance = false,
}) => {
  if (!places?.length) return null;

  return (
    <div className="meet-x__visit-places">
      <h3 className="meet-x__visit-places-heading">{heading}</h3>
      {intro && <p className="meet-x__visit-places-intro">{intro}</p>}
      <div className="meet-x__visit-places-grid" role="list">
        {places.map((place) => (
          <MeetVisitPlaceCard
            key={place.name}
            place={place}
            category={category}
            distancesLoading={distancesLoading}
            venueQuery={venueQuery}
            showDistance={showDistance}
          />
        ))}
      </div>
    </div>
  );
};

export const MeetVisitSection = ({
  visit,
  introHtml,
  distancesLoading = false,
  venueQuery = "",
}) => {
  if (!visit) return null;

  const introBlock = hasVisibleHtml(introHtml) ? (
    <div
      className="meet-x__visit-intro meet-x__richtext meet-x__intro-copy--dark"
      dangerouslySetInnerHTML={{ __html: introHtml }}
    />
  ) : visit.intro ? (
    <p className="meet-x__visit-intro">{visit.intro}</p>
  ) : null;

  const cta = visit.tourismUrl ? (
    <a
      href={visit.tourismUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="meet-x__cta meet-x__visit-cta"
      data-track="meet_tourism_click"
      data-track-location="visit_bardstown"
      data-track-label="visit_bardstown_cta"
      data-track-item="Visit Bardstown"
    >
      Plan your Bardstown visit
    </a>
  ) : null;

  return (
    <div className="meet-x__visit">
      <div className="meet-x__container meet-x__container--wide">
        <header className="meet-x__visit-header">
          <h2 id="visit-heading" className="meet-x__section-label meet-x__section-label--dark">
            {visit.heading}
          </h2>
          {visit.tagline && <p className="meet-x__visit-tagline">{visit.tagline}</p>}
        </header>

        <div className={`meet-x__visit-main${visit.imageUrl ? "" : " meet-x__visit-main--no-image"}`}>
          {visit.imageUrl && (
            <figure className="meet-x__visit-photo">
              <img src={visit.imageUrl} alt={visit.imageAlt} loading="lazy" />
            </figure>
          )}

          <div className="meet-x__visit-lead">
            {introBlock}
            {cta}
          </div>
        </div>

        <div className="meet-x__visit-grid">
          {visit.highlights.map((item) => (
            <article key={item.title} className="meet-x__visit-card">
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>

        <MeetVisitPlacesBlock
          heading={visit.hotelsHeading}
          intro={visit.hotelsIntro}
          places={visit.hotels}
          category="hotel"
          distancesLoading={distancesLoading}
          venueQuery={venueQuery}
          showDistance
        />

        <MeetVisitPlacesBlock
          heading={visit.restaurantsHeading}
          intro={visit.restaurantsIntro}
          places={visit.restaurants}
          category="restaurant"
          distancesLoading={false}
          venueQuery={venueQuery}
        />
      </div>
    </div>
  );
};

export const MeetSponsorCard = ({ sponsor }) => {
  const className = `meet-x__sponsor-slot${
    sponsor.logoUrl ? "" : " meet-x__sponsor-slot--text"
  }${sponsor.url ? " meet-x__sponsor-slot--linked" : ""}`;

  const content = sponsor.logoUrl ? (
    <img src={sponsor.logoUrl} alt={sponsor.name} loading="lazy" />
  ) : (
    <span>{sponsor.name}</span>
  );

  if (sponsor.url) {
    return (
      <a
        href={sponsor.url}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        aria-label={`Visit ${sponsor.name}`}
        data-track="meet_sponsor_click"
        data-track-location="meet_page"
        data-track-item={sponsor.name}
      >
        {content}
      </a>
    );
  }

  return <div className={className}>{content}</div>;
};

export const MeetFeeCards = ({ items }) => {
  if (!items?.length) return null;
  return (
    <div className="meet-x__fee-grid" role="list">
      {items.map((fee) => (
        <div key={fee.label} className="meet-x__fee-card" role="listitem">
          <span className="meet-x__fee-amount">{fee.amount}</span>
          <span className="meet-x__fee-label">{fee.label}</span>
        </div>
      ))}
    </div>
  );
};

export const MeetAdmissionTable = ({ items }) => {
  if (!items?.length) return null;
  return (
    <div className="meet-x__admission-wrap">
      <h3 className="meet-x__structured-heading">Admission prices</h3>
      <div className="meet-x__admission-scroll">
        <table className="meet-x__admission-table">
          <thead>
            <tr>
              <th scope="col">Category</th>
              <th scope="col">Age</th>
              <th scope="col">Daily</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={`${row.category}-${row.ageRange}`}>
                <td>{row.category}</td>
                <td>{row.ageRange}</td>
                <td>{row.daily}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const MeetRegistrationSteps = ({ steps }) => {
  if (!steps?.length) return null;
  return (
    <div className="meet-x__steps">
      <ol className="meet-x__steps-list">
        {steps.map((step) => (
          <li key={step.step} className="meet-x__step">
            <div className="meet-x__step-head">
              <span className="meet-x__step-num">{step.step}</span>
              <h3 className="meet-x__step-title">{step.title}</h3>
            </div>
            <ul className="meet-x__step-items">
              {step.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
};

export const MeetContactCards = ({ items }) => {
  if (!items?.length) return null;
  return (
    <div className="meet-x__contacts-grid">
      {items.map((contact) => (
        <article key={contact.name} className="meet-x__contact-card">
          <h3 className="meet-x__contact-name">{contact.name}</h3>
          {contact.role && <p className="meet-x__contact-role">{contact.role}</p>}
          <div className="meet-x__contact-links">
            {contact.email && (
              <a
                href={`mailto:${contact.email}`}
                data-track="meet_contact_click"
                data-track-location="meet_page"
                data-track-item={contact.name}
                data-track-label="email"
              >
                {contact.email}
              </a>
            )}
            {contact.phone && (
              <a
                href={`tel:${contact.phone.replace(/\D/g, "")}`}
                data-track="meet_contact_click"
                data-track-location="meet_page"
                data-track-item={contact.name}
                data-track-label="phone"
              >
                {contact.phone}
              </a>
            )}
          </div>
        </article>
      ))}
    </div>
  );
};

export const MeetVenueBlock = ({ venue, mapEmbedUrl }) => (
  <div className="meet-x__venue">
    <address className="meet-x__venue-address">
      <strong>{venue.name}</strong>
      <span>{venue.street}</span>
      <span>{venue.cityStateZip}</span>
      {venue.phone && (
        <a
          href={`tel:${venue.phone.replace(/\D/g, "")}`}
          data-track="meet_venue_click"
          data-track-location="meet_page"
          data-track-label="phone"
        >
          {venue.phone}
        </a>
      )}
    </address>
    <div className="meet-x__venue-map">
      <iframe
        title={`${venue.name} on Google Maps`}
        src={mapEmbedUrl}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
    <a
      href={venue.directionsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="meet-x__venue-directions"
      data-track="meet_venue_click"
      data-track-location="meet_page"
      data-track-label="directions"
    >
      Get directions
    </a>
  </div>
);
